import { z } from 'zod';
import { TenantIdSchema, IsoTimestampSchema } from './common.js';

/**
 * Feature flags per condominio (tenant).
 *
 * Granularity: per-module + sub-features (ia / integrations / red) with
 * parent-inheritance enforced at read time via `isFeatureEnabled()`. The stored
 * document never encodes inheritance — a sub-feature can be stored as `true`
 * but resolve to `false` at runtime if its parent module is off.
 *
 * Defaults:
 *   modules.*     = true
 *   ia.*          = false
 *   integrations.*= false
 *   red.*         = false
 *
 * Source of truth: this schema. The ElectroDB entity uses `type: 'any'` for
 * nested maps.
 */
export const ModuleFlagsSchema = z.object({
  residentes: z.boolean().default(true),
  finanzas: z.boolean().default(true),
  gastos_comunes: z.boolean().default(true),
  visitas: z.boolean().default(true),
  paquetes: z.boolean().default(true),
  espacios_comunes: z.boolean().default(true),
  mantencion: z.boolean().default(true),
  personal: z.boolean().default(true),
  bitacora: z.boolean().default(true),
  comunicaciones: z.boolean().default(true),
  asamblea: z.boolean().default(true),
  mercado: z.boolean().default(true),
});
export type ModuleFlags = z.infer<typeof ModuleFlagsSchema>;

export const IaFlagsSchema = z.object({
  chatbot: z.boolean().default(false),
  prediccion_morosidad: z.boolean().default(false),
  presupuesto: z.boolean().default(false),
  anomalias: z.boolean().default(false),
  resumen_acta: z.boolean().default(false),
});
export type IaFlags = z.infer<typeof IaFlagsSchema>;

export const IntegrationFlagsSchema = z.object({
  webpay: z.boolean().default(false),
  khipu: z.boolean().default(false),
  whatsapp: z.boolean().default(false),
  sii: z.boolean().default(false),
  firma_avanzada: z.boolean().default(false),
});
export type IntegrationFlags = z.infer<typeof IntegrationFlagsSchema>;

export const RedFlagsSchema = z.object({
  marketplace: z.boolean().default(false),
  compras_colectivas: z.boolean().default(false),
  benchmarking: z.boolean().default(false),
});
export type RedFlags = z.infer<typeof RedFlagsSchema>;

/**
 * `updatedBy` holds the actor's identifier — Cognito `sub` (UUID-shaped) for
 * real users, or `PLATFORM_TENANT_ID` (ULID) for the migration script / seed.
 * We intentionally accept any non-empty string up to 64 chars rather than
 * forcing a single format, because the real-world data spans UUIDs, ULIDs,
 * and potentially future machine-identity formats.
 *
 * Previously typed as `UlidSchema`, which caused `FeatureFlagsSchema.parse`
 * to throw on any super_admin PATCH (ctx.userId is a Cognito UUID), poisoning
 * subsequent GETs.
 */
export const ActorIdSchema = z.string().min(1).max(64);

export const FeatureFlagsSchema = z.object({
  tenantId: TenantIdSchema,
  modules: ModuleFlagsSchema,
  ia: IaFlagsSchema,
  integrations: IntegrationFlagsSchema,
  red: RedFlagsSchema,
  updatedAt: IsoTimestampSchema,
  updatedBy: ActorIdSchema,
});
export type FeatureFlags = z.infer<typeof FeatureFlagsSchema>;

/**
 * Deep-partial for PATCH endpoint (§7.3) and CreateCondominio override (§8.2).
 * Composition is explicit — one `.partial()` per group — because Zod's
 * `.deepPartial()` was dropped in v4.
 */
export const FeatureFlagsPatchSchema = z.object({
  modules: ModuleFlagsSchema.partial().optional(),
  ia: IaFlagsSchema.partial().optional(),
  integrations: IntegrationFlagsSchema.partial().optional(),
  red: RedFlagsSchema.partial().optional(),
});
export type FeatureFlagsPatch = z.infer<typeof FeatureFlagsPatchSchema>;

export type FeatureKey =
  | `modules.${keyof ModuleFlags}`
  | `ia.${keyof IaFlags}`
  | `integrations.${keyof IntegrationFlags}`
  | `red.${keyof RedFlags}`;

/**
 * Sub-feature → parent module mapping for inheritance (spec §5.4).
 * If parent module is off, sub-feature resolves to false regardless of its
 * stored value.
 */
export const SUBFEATURE_TO_MODULE: Partial<Record<FeatureKey, keyof ModuleFlags>> = {
  'ia.chatbot': 'comunicaciones',
  'ia.prediccion_morosidad': 'finanzas',
  'ia.presupuesto': 'finanzas',
  'ia.anomalias': 'finanzas',
  'ia.resumen_acta': 'asamblea',
  'integrations.webpay': 'finanzas',
  'integrations.khipu': 'finanzas',
  'integrations.whatsapp': 'comunicaciones',
  'integrations.sii': 'finanzas',
  'integrations.firma_avanzada': 'asamblea',
  'red.marketplace': 'mercado',
  'red.compras_colectivas': 'mercado',
  'red.benchmarking': 'finanzas',
};

export function isFeatureEnabled(flags: FeatureFlags, key: FeatureKey): boolean {
  const parent = SUBFEATURE_TO_MODULE[key];
  if (parent && !flags.modules[parent]) return false;
  const [group, name] = key.split('.') as [keyof FeatureFlags, string];
  return Boolean((flags[group] as Record<string, boolean>)[name]);
}

/**
 * Reserved 26-char ULID used as `tenantId` for the Superadmin user and as
 * `updatedBy` on migration-created default flag rows. Allows the audit log,
 * Pre-Token-Gen Lambda, and seed to distinguish platform-level actors from
 * real tenants.
 *
 * Do NOT generate randomly — this value is shared across seed, Pre-Token-Gen,
 * audit log, and migration.
 *
 * Charset: Crockford base32 `[0-9A-HJKMNP-TV-Z]` — `I`, `L`, `O`, `U` are
 * intentionally excluded (UlidSchema enforces this regex). The literal below
 * uses P/T/F/R/M (all valid) as a mnemonic for "PlaTForM".
 */
export const PLATFORM_TENANT_ID = '01PTFRM0000000000000000000' as const;

/**
 * Defaults-applied FeatureFlags document for a new tenant.
 *
 * Helper used by the create-condominio use-case and the migration script.
 * Callers must supply `tenantId`, `updatedAt`, `updatedBy`.
 */
export function defaultFeatureFlags(args: {
  tenantId: string;
  updatedAt: string;
  updatedBy: string;
}): FeatureFlags {
  return FeatureFlagsSchema.parse({
    tenantId: args.tenantId,
    modules: ModuleFlagsSchema.parse({}),
    ia: IaFlagsSchema.parse({}),
    integrations: IntegrationFlagsSchema.parse({}),
    red: RedFlagsSchema.parse({}),
    updatedAt: args.updatedAt,
    updatedBy: args.updatedBy,
  });
}

/**
 * Deep-merge `patch` into `base`, then re-validate. Used by PATCH handler and
 * CreateCondominio override.
 */
export function applyFeatureFlagsPatch(
  base: FeatureFlags,
  patch: FeatureFlagsPatch,
): FeatureFlags {
  return FeatureFlagsSchema.parse({
    ...base,
    modules: { ...base.modules, ...(patch.modules ?? {}) },
    ia: { ...base.ia, ...(patch.ia ?? {}) },
    integrations: { ...base.integrations, ...(patch.integrations ?? {}) },
    red: { ...base.red, ...(patch.red ?? {}) },
  });
}
